import React from 'react';
import { ActionButton } from '@adobe/react-spectrum';
import BoxExport from '@spectrum-icons/workflow/BoxExport';
import { useSelector } from 'react-redux';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { toast } from 'react-toastify';

// Build a MTGO-compatible "qty name" list from the deck's cards map.
// Rules:
//   - Commander(s) listed first, followed by a blank line separator, then
//     the rest of the deck. Most importers (Moxfield, Archidekt, Manabox)
//     honor this convention for picking up the commander zone.
//   - MDFC / split cards are emitted as one line using the full container
//     name, "Front // Back", rather than two independent lines.
//   - Back-face entries are skipped so a MDFC doesn't get double-counted.
//   - Skip the deck's "back" synthetic entries that have no front-face
//     match (shouldn't happen, but guard against it).
function buildMtgoExport(cards) {
    if (!cards) return '';

    // Group back faces by frontFaceId so we can look up the partner name.
    const backFaceByFrontId = new Map();
    Object.values(cards).forEach((c) => {
        if (c?.isFrontFace === false && c?.frontFaceId) {
            backFaceByFrontId.set(c.frontFaceId, c);
        }
    });

    const fronts = Object.values(cards).filter((c) => c?.isFrontFace !== false);
    const lineFor = (card) => {
        const qty = Number(card?.count || 1);
        const back = backFaceByFrontId.get(card?.id);
        const name = back ? `${card.name} // ${back.name}` : card.name;
        return `${qty} ${name}`;
    };

    const commanders = fronts.filter((c) => c?.isCommander);
    const rest = fronts.filter((c) => !c?.isCommander);

    // Alphabetise the non-commander section — makes the pasted list easier
    // to diff against other tools that sort on import.
    rest.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

    const lines = [];
    commanders.forEach((c) => lines.push(lineFor(c)));
    if (commanders.length && rest.length) lines.push('');
    rest.forEach((c) => lines.push(lineFor(c)));
    return lines.join('\n');
}

async function copyToClipboard(text) {
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    // Fallback for environments without the async Clipboard API
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
}

export function BreadCrumbExportButton(props) {
    const cards = useSelector((s) => s?.deck?.cards);
    const deckName = useSelector((s) => s?.deck?.deck?.name);

    const onExport = async () => {
        const text = buildMtgoExport(cards);
        if (!text) {
            toast('Nothing to export yet — deck is still loading.');
            return;
        }
        try {
            await copyToClipboard(text);
            // Sum the leading qty of each line — a single `7 Mountain`
            // counts as 7 cards, not 1. Otherwise a deck with basics
            // looks like it has fewer cards than it does.
            const cardCount = text
                .split('\n')
                .filter((l) => l.trim().length > 0)
                .reduce((sum, l) => sum + (parseInt(l, 10) || 0), 0);
            logEvent(getAnalytics(), 'deck_export_clipboard', {
                deck: deckName || 'unknown',
                cards: cardCount,
            });
            toast(`Copied ${cardCount} cards to clipboard.`);
        } catch (err) {
            toast('Failed to copy — check clipboard permissions.');
        }
    };

    return (
        <ActionButton
            isDisabled={props?.isDisabled || !cards}
            aria-label="Export decklist"
            onPress={onExport}
        >
            <span className="bcBtnLabel">Export list</span>
            <BoxExport />
        </ActionButton>
    );
}
